/* ==========================================================================
   LONDON REPAIR OUTLET - BUY BOX BEHAVIOUR
   File: assets/lro-buybox.js
   Enhances: sections/lro-buybox.liquid
             snippets/lro-volume-tiers.liquid
             snippets/lro-price-block.liquid
   Styles:   assets/lro-buybox.css

   THE CONTRACT
   ------------
   The buy box is complete before this file is parsed. The form is a real
   product form that posts to /cart/add, the variant select posts normally,
   the number input works on its own, and every price on screen was rendered
   by Liquid from the real figures. If this file 404s or throws, the page
   still sells. What this file adds, in order of value:

     1. Add to basket without leaving the page, with pending, added and error
        states, and a broadcast on the same cart events the rest of the theme
        already listens for. lro-cart.js hears the broadcast and refreshes the
        shipping meter and the drawer; nothing is re-implemented here.
     2. The stepper buttons (rendered hidden by the server, because a dead
        button is worse than no button) and a live running total, so the cost
        of the chosen quantity sits beside the button that charges it.
     3. Tier cards that drive the stepper. The radios are real radios in one
        named group, so the keyboard pattern - roving tab stop, arrow keys -
        is the browser's own. This file only carries the choice into the
        quantity input and back.
     4. The sticky mobile bar, revealed only after the real button has left
        the viewport, observed rather than guessed from scroll position.
     5. Live updates to the free-delivery line and the basket ladder whenever
        the basket changes, whoever changed it.

   MONEY IS NEVER FORMATTED BY HAND. The format string is shop.money_format,
   delivered through the section's JSON config. No currency symbol exists in
   this file. Where the format string is missing, money features stay hidden
   rather than guessing - an absent number beats a wrong one.

   COLLISION SAFETY. lro-cart.js intercepts form[data-lro-add] and lro-pdp.js
   intercepts form[data-lro-quick-add], both delegated at the document. This
   file binds directly to its own form element and to nothing else, so an add
   can never fire twice. Keep it that way.

   NO DEPENDENCIES, NO BUILD STEP, ONE GLOBAL: the load guard below.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lroBuyboxLoaded) return;
  window.__lroBuyboxLoaded = true;

  /* Same list as lro-cart.js: every event name that means "the basket
     changed". Listening for a few extra strings costs nothing. */
  var CART_EVENTS = [
    'cart:update',
    'cart:updated',
    'cart:change',
    'cart:refresh',
    'cart:added',
    'variant:update'
  ];

  /** How long the "Added to basket" state holds before the button reverts. */
  var ADDED_HOLD = 2400;

  /** Cart changes arrive in bursts; recompute once per burst. */
  var REFRESH_DEBOUNCE = 120;

  /* ------------------------------------------------------------------------
     Small helpers
     ---------------------------------------------------------------------- */

  function routeRoot() {
    var root = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        root = window.Shopify.routes.root;
      }
    } catch (error) {
      root = '/';
    }
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var context = this;
      var args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(context, args);
      }, wait);
    };
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isFinite(n) ? n : 0;
  }

  /** {key} and [key] both substitute, same convention as lro-cart.js and the
   *  Liquid copy deck. */
  function interpolate(template, values) {
    return String(template == null ? '' : template).replace(/\{(\w+)\}|\[(\w+)\]/g, function (whole, braceKey, bracketKey) {
      var key = braceKey || bracketKey;
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole;
    });
  }

  /** Replaces the first non-empty text node inside el, leaving any element
   *  children (the visually hidden prefix, the unit suffix) untouched. */
  function setFirstText(el, text) {
    if (!el) return;
    for (var i = 0; i < el.childNodes.length; i += 1) {
      var node = el.childNodes[i];
      if (node.nodeType === 3 && String(node.nodeValue).replace(/\s/g, '') !== '') {
        node.nodeValue = text;
        return;
      }
    }
    el.insertBefore(document.createTextNode(text), el.firstChild);
  }

  /** The same, from the end. */
  function setLastText(el, text) {
    if (!el) return;
    for (var i = el.childNodes.length - 1; i >= 0; i -= 1) {
      var node = el.childNodes[i];
      if (node.nodeType === 3 && String(node.nodeValue).replace(/\s/g, '') !== '') {
        node.nodeValue = text;
        return;
      }
    }
    el.appendChild(document.createTextNode(text));
  }

  /* ------------------------------------------------------------------------
     Money - the format string comes from shop.money_format via the config.
     Identical treatment to lro-cart.js so two components can never print the
     same pence two different ways.
     ---------------------------------------------------------------------- */

  function groupDigits(pence, precision, thousands, decimal) {
    var number = (Number(pence) || 0) / 100;
    var fixed = number.toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(pence, format) {
    var template = String(format == null ? '' : format);
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : '';
    var value;

    switch (token) {
      case 'amount_no_decimals':
        value = groupDigits(pence, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = groupDigits(pence, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = groupDigits(pence, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = groupDigits(pence, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = groupDigits(pence, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = groupDigits(pence, 2, "'", '.');
        break;
      default:
        value = groupDigits(pence, 2, ',', '.');
    }

    if (match) return template.replace(match[0], value);

    try {
      var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (currency) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: currency
        }).format((Number(pence) || 0) / 100);
      }
    } catch (error) {
      /* fall through to the bare number */
    }
    return value;
  }

  /* ------------------------------------------------------------------------
     Shared cart plumbing

     One registry of update functions across every root on the page. When the
     basket changes - through this file, lro-cart.js, Horizon's drawer or a
     back-forward cache restore - each registered function receives the fresh
     cart and repaints its own free-delivery line and ladder.
     ---------------------------------------------------------------------- */

  var cartUpdaters = [];
  var broadcasting = false;

  function fetchCart() {
    if (!('fetch' in window)) return Promise.reject(new Error('no fetch'));
    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js ' + response.status);
      return response.json();
    });
  }

  function applyCartAll(cart) {
    if (!cart) return;
    for (var i = 0; i < cartUpdaters.length; i += 1) {
      try {
        cartUpdaters[i](cart);
      } catch (error) {
        /* One stale root must not stop the rest. */
      }
    }
  }

  var refreshSoon = debounce(function () {
    fetchCart().then(applyCartAll).catch(function () {
      /* The server-rendered lines stay on screen. Nothing to undo. */
    });
  }, REFRESH_DEBOUNCE);

  /** Tell the rest of the theme. lro-cart.js and Horizon both listen for
   *  cart:update; the extras cost nothing. The flag stops this file reacting
   *  to its own announcement. */
  function broadcast(cart) {
    broadcasting = true;
    var detail = {
      source: 'lro-buybox',
      itemCount: cart ? cart.item_count : undefined,
      cart: cart,
      data: { source: 'lro-buybox', cart: cart }
    };
    ['cart:update', 'cart:refresh', 'cart:change'].forEach(function (name) {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
      } catch (error) {
        /* No CustomEvent, no listeners worth reaching. */
      }
    });
    window.setTimeout(function () {
      broadcasting = false;
    }, 0);
  }

  /** Same candidates as lro-cart.js. A drawer that will not open is not a
   *  reason to fail an add. */
  function openCartDrawer() {
    var candidates = ['cart-drawer-component', '[data-lro-cart-drawer]', '#cart-drawer', '.cart-drawer'];
    for (var i = 0; i < candidates.length; i += 1) {
      var node = document.querySelector(candidates[i]);
      if (!node) continue;
      try {
        if (typeof node.showDialog === 'function') return node.showDialog(), true;
        if (typeof node.open === 'function') return node.open(), true;
        var dialog = node.matches && node.matches('dialog') ? node : node.querySelector('dialog');
        if (dialog && typeof dialog.showModal === 'function') return dialog.showModal(), true;
      } catch (error) {
        /* try the next candidate */
      }
    }
    return false;
  }

  /* ------------------------------------------------------------------------
     Opening hours - "Open now" beside the collect heading.
     The shop is in Brixton, so the check runs on London time wherever the
     visitor is. If the timezone database is unavailable the visitor's local
     clock is close enough for a UK parts shop; if the hours string does not
     parse, the badge simply stays hidden.
     ---------------------------------------------------------------------- */

  function londonNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date());
      var out = {};
      for (var i = 0; i < parts.length; i += 1) {
        out[parts[i].type] = parts[i].value;
      }
      var days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      var day = days[out.weekday];
      var hour = parseInt(out.hour, 10);
      var minute = parseInt(out.minute, 10);
      if (day == null || !isFinite(hour) || !isFinite(minute)) return null;
      return { day: day, minutes: hour * 60 + minute };
    } catch (error) {
      var d = new Date();
      return { day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
    }
  }

  function parseHours(spec) {
    var m = /^\s*(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*$/.exec(String(spec || ''));
    if (!m) return null;
    var start = Number(m[1]) * 60 + Number(m[2]);
    var end = Number(m[3]) * 60 + Number(m[4]);
    if (!(end > start)) return null;
    return { start: start, end: end };
  }

  /* ------------------------------------------------------------------------
     The ladder and the free-delivery line, shared by the buy box and any
     standalone tier block.
     ---------------------------------------------------------------------- */

  var LADDER_PROGRESS = '{count} in your basket. Add {needed} more and {percent}% comes off the lot.';
  var LADDER_UNLOCKED = '{percent}% is coming off your whole basket. {count} items in it so far.';

  function updateLadder(tiersRoot, cart, labels) {
    var ladder = tiersRoot.querySelector('[data-lro-ladder]');
    if (!ladder || !cart) return;
    var tq = toInt(tiersRoot.getAttribute('data-lro-tiers-threshold'));
    var tp = toInt(tiersRoot.getAttribute('data-lro-tiers-percent'));
    if (tq <= 0) return;

    var count = Math.max(0, toInt(cart.item_count));
    var unlocked = count >= tq;
    ladder.setAttribute('data-lro-ladder-count', String(count));
    ladder.classList.toggle('is-unlocked', unlocked);

    var segs = ladder.querySelectorAll('[data-lro-seg]');
    var segments = segs.length;
    var filled = segments > 0 ? Math.min(segments, Math.floor((count * segments) / tq)) : 0;
    for (var i = 0; i < segs.length; i += 1) {
      segs[i].classList.toggle('is-on', unlocked || i < filled);
    }

    var text = ladder.querySelector('[data-lro-ladder-text]');
    if (text) {
      text.textContent = unlocked
        ? interpolate(labels.ladderUnlocked || LADDER_UNLOCKED, { count: count, percent: tp })
        : interpolate(labels.ladderProgress || LADDER_PROGRESS, { count: count, needed: tq - count, percent: tp });
    }
  }

  /* ------------------------------------------------------------------------
     THE BUY BOX
     ---------------------------------------------------------------------- */

  function initBuybox(root) {
    if (root.__lroBb) return;
    root.__lroBb = true;

    var configNode = root.querySelector('script[type="application/json"][data-lro-bb-config]');
    var config = {};
    if (configNode) {
      try {
        config = JSON.parse(configNode.textContent) || {};
      } catch (error) {
        config = {};
      }
    }
    var labels = config.labels || {};
    var moneyFormat = config.moneyFormat || '';

    function money(pence) {
      return formatMoney(pence, moneyFormat);
    }

    var form = document.getElementById(root.getAttribute('data-lro-bb-form') || '');
    var qtyInput = document.getElementById(root.getAttribute('data-lro-bb-qty') || '');
    var tiersRoot = root.querySelector('[data-lro-tiers]');
    if (tiersRoot) tiersRoot.__lroTiers = true; /* claimed; not standalone */

    var threshold = tiersRoot ? toInt(tiersRoot.getAttribute('data-lro-tiers-threshold')) : 0;
    var percent = tiersRoot ? toInt(tiersRoot.getAttribute('data-lro-tiers-percent')) : 0;

    var unit = toInt(config.unit);
    var available = config.available !== false;
    var variants = config.variants || [];

    var statusNode = root.querySelector('[data-lro-bb-status]');
    var errorNode = root.querySelector('[data-lro-bb-error]');
    var running = root.querySelector('[data-lro-bb-running]');
    var runningText = root.querySelector('[data-lro-bb-running-text]');
    var sticky = root.querySelector('[data-lro-bb-sticky]');
    var stickyPrice = root.querySelector('[data-lro-bb-sticky-price]');
    var addButton = root.querySelector('[data-lro-bb-add]');
    var stickyAdd = root.querySelector('[data-lro-bb-sticky-add]');
    var addButtons = [addButton, stickyAdd].filter(Boolean);
    var labelSlots = root.querySelectorAll('[data-lro-bb-add-label]');
    var adding = false;
    var addedTimer = null;

    function announce(message) {
      if (!statusNode || !message) return;
      statusNode.textContent = '';
      window.setTimeout(function () {
        statusNode.textContent = message;
      }, 60);
    }

    /* ---- Quantity ------------------------------------------------------- */

    function maxQty() {
      if (!qtyInput) return Infinity;
      var m = parseInt(qtyInput.getAttribute('max') || '', 10);
      return isFinite(m) && m > 0 ? m : Infinity;
    }

    function readQty() {
      if (!qtyInput) return 1;
      var n = parseInt(qtyInput.value, 10);
      if (!isFinite(n) || n < 1) return 1;
      return Math.min(n, maxQty());
    }

    /** The tier arithmetic, exactly as the Liquid computes it: the discount
     *  only ever applies at or past the threshold, the pound-total is exact,
     *  and the per-part figure is rounded to the nearest penny. */
    function totalsFor(q) {
      var gross = unit * q;
      if (threshold > 0 && percent > 0 && q >= threshold) {
        var off = Math.round((gross * percent) / 100);
        var total = gross - off;
        return { total: total, each: Math.round(total / q), discounted: true };
      }
      return { total: gross, each: unit, discounted: false };
    }

    function renderRunning() {
      if (!runningText || !moneyFormat) return;
      var q = readQty();
      var t = totalsFor(q);
      var template = labels.runningTotal || '{qty} x {each} = {total}';
      var parts = String(template).split(/\{total\}|\[total\]/);
      runningText.textContent = '';
      for (var i = 0; i < parts.length; i += 1) {
        var chunk = interpolate(parts[i], { qty: q, each: money(t.each) });
        if (chunk) runningText.appendChild(document.createTextNode(chunk));
        if (i < parts.length - 1) {
          var strong = document.createElement('b');
          strong.className = 'lro-price lro-price--sm';
          strong.textContent = money(t.total);
          runningText.appendChild(strong);
        }
      }
    }

    function syncTierChoice() {
      if (!tiersRoot) return;
      var q = readQty();
      var inputs = tiersRoot.querySelectorAll('[data-lro-tier-input]');
      var match = null;
      for (var i = 0; i < inputs.length; i += 1) {
        var v = toInt(inputs[i].value);
        if (threshold > 0 && q >= threshold && v >= threshold) match = inputs[i];
        else if (v === q) match = inputs[i];
      }
      for (var j = 0; j < inputs.length; j += 1) {
        inputs[j].checked = inputs[j] === match;
        var card = inputs[j].closest('[data-lro-tier]');
        if (card) card.classList.toggle('is-selected', inputs[j] === match);
      }
    }

    var stepDown = form ? form.querySelector('[data-lro-bb-step="-1"]') : null;
    var stepUp = form ? form.querySelector('[data-lro-bb-step="1"]') : null;

    function refreshStepper() {
      var q = readQty();
      if (stepDown) stepDown.disabled = q <= 1;
      if (stepUp) stepUp.disabled = q >= maxQty();
    }

    function refresh() {
      renderRunning();
      syncTierChoice();
      refreshStepper();
    }

    function clearError() {
      if (errorNode && !errorNode.hidden) errorNode.hidden = true;
    }

    function setQty(n) {
      if (!qtyInput) return;
      var clamped = Math.max(1, Math.min(isFinite(n) && n > 0 ? Math.floor(n) : 1, maxQty()));
      qtyInput.value = String(clamped);
      clearError();
      refresh();
    }

    /* ---- Stepper wiring ------------------------------------------------- */

    if (qtyInput) {
      if (stepDown) {
        stepDown.hidden = false;
        stepDown.addEventListener('click', function () {
          setQty(readQty() - 1);
        });
      }
      if (stepUp) {
        stepUp.hidden = false;
        stepUp.addEventListener('click', function () {
          setQty(readQty() + 1);
        });
      }

      /* Numeric guard while typing: digits only, but an empty field is left
         alone until change/blur normalises it, so mid-edit is never fought. */
      qtyInput.addEventListener('input', function () {
        var cleaned = qtyInput.value.replace(/[^0-9]/g, '');
        if (cleaned !== qtyInput.value) qtyInput.value = cleaned;
        if (cleaned !== '') refresh();
      });

      qtyInput.addEventListener('change', function () {
        setQty(readQty());
      });
    }

    /* ---- Reveal the running total only once it can be kept accurate ----- */

    if (running && runningText && moneyFormat && qtyInput) {
      running.setAttribute('aria-live', 'polite');
      running.setAttribute('aria-atomic', 'true');
      running.hidden = false;
    }

    /* ---- Tier cards drive the stepper ----------------------------------- */

    if (tiersRoot && qtyInput) {
      tiersRoot.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || !input.matches || !input.matches('[data-lro-tier-input]')) return;
        setQty(toInt(input.value));
      });
    }

    /* ---- Variant switching ---------------------------------------------- */

    function findVariant(id) {
      for (var i = 0; i < variants.length; i += 1) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    function setAvailability(ok) {
      available = ok;
      for (var i = 0; i < addButtons.length; i += 1) {
        addButtons[i].disabled = !ok;
      }
      for (var j = 0; j < labelSlots.length; j += 1) {
        labelSlots[j].textContent = ok ? labels.add || 'Add to basket' : labels.soldOut || 'Out of stock';
      }
    }

    /** Repaints every tier card from the new unit price, with the same
     *  rounding the server used, so the cards and the basket can never
     *  disagree by more than the documented penny rounding. */
    function repriceTiers() {
      if (!tiersRoot) return;
      tiersRoot.setAttribute('data-lro-tiers-unit', String(unit));
      if (!moneyFormat) return;
      var cards = tiersRoot.querySelectorAll('[data-lro-tier]');
      for (var i = 0; i < cards.length; i += 1) {
        var q = toInt(cards[i].getAttribute('data-lro-tier-qty'));
        if (q < 1) continue;
        var t = totalsFor(q);
        cards[i].setAttribute('data-lro-tier-total', String(t.total));
        setFirstText(cards[i].querySelector('.lro-tiers__each'), money(t.each));
        setLastText(cards[i].querySelector('.lro-tiers__total'), money(t.total) + ' total');
        var save = cards[i].querySelector('.lro-tiers__save');
        if (save && t.discounted && unit - t.each > 0) {
          save.textContent = percent + '% off, ' + money(unit - t.each) + ' back on every part';
        }
      }
    }

    /** Recomputes the saving claim against the benchmark for the new price.
     *  Anything that can no longer be substantiated is hidden rather than
     *  left stale: an absent claim beats a wrong one. */
    function repriceBenchmark() {
      if (!moneyFormat) return;
      var blocks = root.querySelectorAll('[data-lro-pb][data-lro-pb-benchmark]');
      for (var i = 0; i < blocks.length; i += 1) {
        var pb = blocks[i];
        var bench = toInt(pb.getAttribute('data-lro-pb-benchmark'));
        if (bench <= 0) continue;
        var saving = bench - unit;

        var wrap = pb.querySelector('.lro-pb__save');
        var chip = pb.querySelector('.lro-pb__chip');
        if (wrap && chip) {
          var isSaveChip = chip.classList.contains('lro-save');
          if (saving > 0 && isSaveChip) {
            var pct = Math.round((saving * 100) / bench);
            if (pct < 1 || pct > 95) pct = 0;
            chip.textContent = pct > 0 ? 'You save ' + money(saving) + ', ' + pct + '% off' : 'You save ' + money(saving);
            wrap.hidden = false;
            pb.setAttribute('data-lro-state', 'save');
          } else if (saving === 0 && !isSaveChip) {
            wrap.hidden = false;
            pb.setAttribute('data-lro-state', 'match');
          } else {
            wrap.hidden = true;
            pb.setAttribute('data-lro-state', 'plain');
          }
        }

        var bulk = pb.querySelector('.lro-pb__bulk');
        if (bulk && threshold > 1 && percent > 0) {
          var t = totalsFor(threshold);
          var ours = bulk.querySelector('.lro-pb__bulk-cell--ours .lro-pb__bulk-value');
          if (ours) ours.textContent = money(t.total);
          var bulkSave = bulk.querySelector('.lro-pb__bulk-save');
          if (bulkSave) {
            var better = bench * threshold - t.total;
            if (better > 0) {
              var inner = bulkSave.querySelector('.lro-save--text');
              (inner || bulkSave).textContent = money(better) + ' better off on the ' + threshold;
              bulkSave.hidden = false;
            } else {
              bulkSave.hidden = true;
            }
          }
        }
      }
    }

    var variantControl = form ? form.querySelector('[data-lro-bb-variant]') : null;
    if (variantControl && variantControl.tagName === 'SELECT') {
      variantControl.addEventListener('change', function () {
        var variant = findVariant(variantControl.value);
        if (!variant) return;
        unit = toInt(variant.price);
        clearError();

        if (moneyFormat) {
          var nows = root.querySelectorAll('[data-lro-pb-now]');
          for (var i = 0; i < nows.length; i += 1) {
            nows[i].textContent = money(unit);
          }
          if (stickyPrice) stickyPrice.textContent = money(unit);
        }

        var skuNode = root.querySelector('[data-lro-bb-sku]');
        if (skuNode) skuNode.textContent = String(variant.sku || '');

        if (qtyInput) {
          var tracked = String(variant.tracked || '') !== '';
          var qtyLeft = toInt(variant.quantity);
          if (tracked && variant.policy === 'deny' && qtyLeft > 0) {
            qtyInput.setAttribute('max', String(qtyLeft));
          } else {
            qtyInput.removeAttribute('max');
          }
          setQty(readQty());
        }

        repriceTiers();
        repriceBenchmark();
        setAvailability(variant.available !== false);
        refresh();
      });
    }

    /* ---- Add to basket -------------------------------------------------- */

    function setButtons(mode) {
      for (var i = 0; i < addButtons.length; i += 1) {
        var button = addButtons[i];
        if (mode === 'busy') {
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('data-lro-state', 'busy');
          button.disabled = true;
        } else {
          button.removeAttribute('aria-busy');
          button.disabled = !available;
          if (mode === 'added') button.setAttribute('data-lro-state', 'added');
          else button.removeAttribute('data-lro-state');
        }
      }
      var text = labels.add || 'Add to basket';
      if (mode === 'busy') text = labels.adding || 'Adding';
      if (mode === 'added') text = labels.added || 'Added to basket';
      if (mode === 'idle' && !available) text = labels.soldOut || 'Out of stock';
      for (var j = 0; j < labelSlots.length; j += 1) {
        labelSlots[j].textContent = text;
      }
    }

    function showError(message) {
      if (errorNode) {
        errorNode.textContent = message;
        errorNode.hidden = false;
      }
      announce(message);
    }

    if (form && addButton && 'fetch' in window && typeof FormData === 'function') {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (adding || !available) return;
        if (addedTimer) {
          clearTimeout(addedTimer);
          addedTimer = null;
        }
        if (qtyInput) setQty(readQty()); /* clamp before the body is read */
        clearError();

        adding = true;
        setButtons('busy');

        var addUrl = String(config.cartAddUrl || routeRoot() + 'cart/add').replace(/(\.js)?$/, '.js');

        fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: new FormData(form)
        })
          .then(function (response) {
            return response.json().then(function (payload) {
              if (!response.ok) throw payload;
              return payload;
            });
          })
          .then(function () {
            adding = false;
            setButtons('added');
            announce(labels.added || 'Added to basket');
            addedTimer = window.setTimeout(function () {
              addedTimer = null;
              setButtons('idle');
            }, ADDED_HOLD);

            /* The basket totals are the authority: re-read, repaint our own
               lines, then tell everyone else. lro-cart.js takes the event
               from here and refreshes the meters and the drawer. */
            return fetchCart()
              .then(function (cart) {
                applyCartAll(cart);
                broadcast(cart);
                if (config.openDrawer) openCartDrawer();
              })
              .catch(function () {
                broadcast(null);
              });
          })
          .catch(function (payload) {
            adding = false;
            setButtons('idle');
            showError(
              (payload && (payload.description || payload.message)) ||
                labels.error ||
                'That did not add. Try again, or ring the shop.'
            );
          });
      });
    }

    /* ---- Sticky mobile bar ---------------------------------------------- */

    if (sticky && addButton && 'IntersectionObserver' in window) {
      /* Removing [hidden] hands control to the stylesheet, which keeps the
         bar translated off screen and invisible until .is-visible. */
      sticky.hidden = false;
      var observer = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      });
      observer.observe(addButton);
    }

    /* ---- Open now ------------------------------------------------------- */

    (function initOpenNow() {
      var box = root.querySelector('[data-lro-bb-collect]');
      var badge = root.querySelector('[data-lro-bb-open]');
      if (!box || !badge) return;
      var now = londonNow();
      if (!now) return;
      var spec = now.day === 0 ? box.getAttribute('data-lro-hours-sun') : box.getAttribute('data-lro-hours-monsat');
      var range = parseHours(spec);
      if (!range) return;
      var open = now.minutes >= range.start && now.minutes < range.end;
      badge.textContent = open ? labels.openNow || 'Open now' : labels.closedNow || 'Closed now';
      badge.setAttribute('data-lro-state', open ? 'open' : 'closed');
      badge.hidden = false;
    })();

    /* ---- Live basket lines ---------------------------------------------- */

    cartUpdaters.push(function (cart) {
      if (!root.isConnected) return;

      var row = root.querySelector('[data-lro-bb-freeship]');
      if (row) {
        var freeOver = toInt(row.getAttribute('data-lro-bb-threshold'));
        if (freeOver > 0) {
          var total = Math.max(0, toInt(cart.total_price));
          var unlocked = total >= freeOver;
          row.setAttribute('data-lro-bb-total', String(total));
          row.classList.toggle('is-unlocked', unlocked);
          var text = row.querySelector('[data-lro-bb-freeship-text]');
          if (text) {
            if (unlocked) {
              text.textContent = labels.freeUnlocked || 'Your basket qualifies for free UK delivery.';
            } else if (moneyFormat) {
              text.textContent = interpolate(
                labels.freeProgress || 'Add {amount} for free UK delivery, or it is charged at checkout.',
                { amount: money(freeOver - total) }
              );
            }
          }
        }
      }

      if (tiersRoot) updateLadder(tiersRoot, cart, labels);
    });

    /* ---- First paint ---------------------------------------------------- */

    setAvailability(available);
    refresh();
  }

  /* ------------------------------------------------------------------------
     Standalone tier blocks - the snippet rendered outside a buy box. The
     radios still drive whatever quantity input they were pointed at, and the
     ladder still follows the basket. Prices are server-rendered and stay
     that way: with no variant switching there is nothing to reprice.
     ---------------------------------------------------------------------- */

  function initTiersStandalone(tiersRoot) {
    if (tiersRoot.__lroTiers) return;
    tiersRoot.__lroTiers = true;

    var target = document.getElementById(tiersRoot.getAttribute('data-lro-tiers-target') || '');

    tiersRoot.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-lro-tier-input]')) return;
      var inputs = tiersRoot.querySelectorAll('[data-lro-tier-input]');
      for (var i = 0; i < inputs.length; i += 1) {
        var card = inputs[i].closest('[data-lro-tier]');
        if (card) card.classList.toggle('is-selected', inputs[i].checked);
      }
      if (target) {
        var q = toInt(input.value);
        if (q >= 1) {
          target.value = String(q);
          try {
            target.dispatchEvent(new Event('change', { bubbles: true }));
          } catch (error) {
            /* The value is set; the owner of the input may not be listening. */
          }
        }
      }
    });

    cartUpdaters.push(function (cart) {
      if (!tiersRoot.isConnected) return;
      updateLadder(tiersRoot, cart, {});
    });
  }

  /* ------------------------------------------------------------------------
     Wiring
     ---------------------------------------------------------------------- */

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;

    var roots = [];
    if (context !== document && context.matches && context.matches('[data-lro-bb]')) roots.push(context);
    var found = context.querySelectorAll('[data-lro-bb]');
    for (var i = 0; i < found.length; i += 1) roots.push(found[i]);
    for (var j = 0; j < roots.length; j += 1) initBuybox(roots[j]);

    var tiers = context.querySelectorAll('[data-lro-tiers]');
    for (var t = 0; t < tiers.length; t += 1) initTiersStandalone(tiers[t]);
  }

  function start() {
    boot(document);

    /* The listeners are bound whether or not a root exists yet, because the
       theme editor can inject the section later. Every handler goes through
       the registry, which is a no-op while the registry is empty, so a page
       with no buy box never fetches a thing. */
    CART_EVENTS.forEach(function (name) {
      document.addEventListener(name, function (event) {
        if (broadcasting || !cartUpdaters.length) return;
        var detail = event && event.detail;
        var cart = detail && (detail.cart || (detail.data && detail.data.cart));
        if (cart && typeof cart.item_count === 'number' && typeof cart.total_price === 'number') {
          applyCartAll(cart);
        } else {
          refreshSoon();
        }
      });
    });

    /* Restoring from the back/forward cache hands back a stale basket. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && cartUpdaters.length) refreshSoon();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* The theme editor re-renders the section; init whatever arrived. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})();
